import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChartLine, FaArrowUp, FaArrowDown, FaExchangeAlt, FaShieldAlt, FaRocket } from 'react-icons/fa';
import { io } from 'socket.io-client';
import { usePrices } from '../../../context/PriceContext';

const extractPrice = (val) => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') return parseFloat(val);
    if (Array.isArray(val)) return parseFloat(val[5] || val[0] || 0); 
    if (typeof val === 'object' && val !== null) {
        return parseFloat(val.lp || val.price || val.last_price || val.mark_price || val.data || 0);
    }
    return 0;
};

const PriceFlash = ({ value, prefix = "$" }) => {
    const numericValue = extractPrice(value);
    const [prevValue, setPrevValue] = useState(numericValue);
    const [flash, setFlash] = useState('');

    useEffect(() => {
        if (numericValue > prevValue) setFlash('flash-green');
        else if (numericValue < prevValue) setFlash('flash-red');
        
        if (numericValue !== prevValue) {
            const timer = setTimeout(() => setFlash(''), 1000);
            setPrevValue(numericValue);
            return () => clearTimeout(timer);
        }
    }, [numericValue]);

    return (
        <span className={`price-text ${flash}`}>
            {prefix}{numericValue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
    );
};

const CoinSwitchDashboard = () => {
    const [asset, setAsset] = useState('BTC');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const prices = usePrices();
    const livePriceFromAppSocket = asset === 'BTC' ? prices['BTCUSD'] : prices['ETHUSD'];

    const TRADE_URL = window.location.hostname === "localhost" 
        ? "http://localhost:8000" 
        : `${window.location.protocol}//${window.location.hostname}:8000`;

    useEffect(() => {
        const tradeSocket = io(TRADE_URL);
        
        tradeSocket.on('connect', () => {
            tradeSocket.emit('get_coinswitch_data', asset);
        });

        tradeSocket.on('coinswitch_update', (update) => {
            if (update.asset === asset) {
                setData(update);
                setLoading(false);
                setError(null);
            }
        });

        tradeSocket.on('connect_error', () => {
            setError('Waiting for connection to trade server (Port 8000)...');
        });

        return () => tradeSocket.disconnect();
    }, [asset]);

    return (
        <motion.div 
            className="container-fluid py-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ 
                background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
                minHeight: '100vh',
                color: '#f8fafc'
            }}
        >
            {/* Header Section */}
            <div className="row mb-4">
                <div className="col-12 d-flex justify-content-between align-items-center">
                    <div>
                        <h2 className="fw-bold mb-1 d-flex align-items-center">
                            <img src="https://coinswitch.co/favicon.ico" alt="CoinSwitch" className="me-2" style={{ width: '32px', height: '32px' }} />
                            CoinSwitch Pro Terminal
                        </h2>
                        <p className="text-white-50 small mb-0">High-speed market tracking & execution</p>
                    </div>
                    <div className="btn-group p-1" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                        <button className={`btn btn-sm px-4 ${asset === 'BTC' ? 'btn-primary' : 'text-white'}`} onClick={() => setAsset('BTC')}>BTC</button>
                        <button className={`btn btn-sm px-4 ${asset === 'ETH' ? 'btn-primary' : 'text-white'}`} onClick={() => setAsset('ETH')}>ETH</button>
                    </div>
                </div>
            </div>

            {/* Price Row */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-0 shadow-lg" style={{ background: 'rgba(255, 255, 255, 0.03)', backdropFilter: 'blur(10px)', borderRadius: '24px' }}>
                        <div className="card-body p-5 text-center">
                            <h6 className="text-white-50 text-uppercase fw-bold mb-3 ls-wide">Current Market Price</h6>
                            <h1 className="display-1 fw-bold mb-4">
                                <PriceFlash value={data?.last_price || livePriceFromAppSocket || 0} />
                            </h1>
                            <div className="d-flex justify-content-center gap-4">
                                <div className="badge bg-success-subtle text-success p-3 rounded-pill">
                                    <FaArrowUp className="me-1" /> High: ${data?.stats?.high?.toLocaleString()}
                                </div>
                                <div className="badge bg-danger-subtle text-danger p-3 rounded-pill">
                                    <FaArrowDown className="me-1" /> Low: ${data?.stats?.low?.toLocaleString()}
                                </div>
                                <div className="badge bg-primary-subtle text-primary p-3 rounded-pill">
                                    <FaRocket className="me-1" /> Vol: {data?.stats?.volume?.toLocaleString()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Trade Controls */}
            <div className="row g-4">
                <div className="col-md-6">
                    <button className="btn btn-lg w-100 py-4 fw-bold text-white shadow-lg" style={{ background: 'linear-gradient(to right, #10b981, #059669)', borderRadius: '20px', border: 'none' }}>
                        INSTANT BUY {asset}
                    </button>
                </div>
                <div className="col-md-6">
                    <button className="btn btn-lg w-100 py-4 fw-bold text-white shadow-lg" style={{ background: 'linear-gradient(to right, #ef4444, #dc2626)', borderRadius: '20px', border: 'none' }}>
                        INSTANT SELL {asset}
                    </button>
                </div>
            </div>

            <style>{`
                .price-text { transition: color 0.3s ease; }
                .flash-green { color: #22c55e !important; }
                .flash-red { color: #ef4444 !important; }
                .ls-wide { letter-spacing: 2px; }
            `}</style>
        </motion.div>
    );
};

export default CoinSwitchDashboard;
